// ============================================================
// SkyPlus Tenant Loader  (auth-aware, post-login version)
// app/components/skyplus/shared/sector/tenant-loader.service.ts
//
// Fetches GET /api/tenant/config with the same Bearer-token pattern
// used by MenuService. Call loadTenant() once from SpHeaderComponent
// after login, not from APP_INITIALIZER.
//
// FAIL-SAFE: on any error (401, timeout, malformed response) the
// SectorService stays on the defaults it was constructed with
// (client mode, all features off). A broken tenant endpoint never
// reveals demo features on a client site.
// ============================================================

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthenticationService,AppConfigService } from '@alfresco/adf-core';
import { firstValueFrom, timeout, catchError, of } from 'rxjs';
import { SectorService } from './sector.service';
import { TenantResponse, DEFAULT_FEATURES, configBrand } from './sector.model';
import { GENERIC_PACK } from './packs/generic.pack';

@Injectable({ providedIn: 'root' })
export class TenantLoader {
  private readonly endpoint: string;


  private loaded = false;

  constructor(
    private http: HttpClient,
    private sector: SectorService,
    private authService: AuthenticationService,
    private appConfig: AppConfigService
  ) {
    const apiBase = this.appConfig.get<string>('skyplus.apiUrl', '/bpm/api');
    this.endpoint = `${apiBase}/tenant/config`;
  }

  /**
   * Call from SpHeaderComponent.ngOnInit(), same pattern as
   * menuService.loadMenu(). Idempotent — safe to call multiple times.
   */
  async loadTenant(forceReload = false): Promise<void> {
    if (this.loaded && !forceReload) return;

    try {
      const headers = this.getAuthHeaders();

      const response = await firstValueFrom(
        this.http.get<TenantResponse>(this.endpoint, { headers }).pipe(
          timeout(4000),
          catchError(err => {
            console.warn('[TenantLoader] Falling back to safe defaults:', err?.status || err?.message || err);
            return of(null);
          })
        )
      );

      if (response?.pack) {
        this.sector.setFromResponse(response);
        this.sector.applyBrand();
        this.loaded = true;
        console.log(
          `[TenantLoader] Tenant=${response.tenantId} mode=${response.tenantMode} ` +
          `pack=${response.pack.packName} features=${this.activeFeatureList(response.features)}`
        );
      } else {
        this.sector.setFromResponse(this.safeDefaults());
        this.sector.applyBrand();
        console.log('[TenantLoader] Using safe defaults (client mode, SkyPlus brand).');
      }
    } catch (err) {
      console.warn('[TenantLoader] Unexpected error, using safe defaults:', err);
      this.sector.setFromResponse(this.safeDefaults());
      this.sector.applyBrand();
    }
  }

  private getAuthHeaders(): HttpHeaders {
    try {
      const token = this.authService.getToken();
      return token
        ? new HttpHeaders({ Authorization: `Bearer ${token}` })
        : new HttpHeaders();
    } catch {
      return new HttpHeaders();
    }
  }

  private safeDefaults(): TenantResponse {
    return {
      tenantId:       'default',
      tenantName:     'Default',
      tenantMode:     'client',
      brand:          configBrand(this.appConfig),
      features:       { ...DEFAULT_FEATURES },
      pack:           GENERIC_PACK,
      availablePacks: [],
      loadedAt:       new Date().toISOString()
    };
  }

  private activeFeatureList(features: any): string {
    if (!features) return '[none]';
    const enabled = Object.entries(features)
      .filter(([_, v]) => v === true)
      .map(([k, _]) => k);
    return enabled.length ? enabled.join(',') : '[none]';
  }
}